import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import Suggest from "components/Controls/MultiSelect/components/Suggest";
import { defaultNoResultsRenderer } from "components/Controls/MultiSelect/utils";
import TextTruncate from "components/TextTruncate";
import { INPUT } from "shared/labels";
import { isNotEmpty } from "utils/validate";
import SearchIcon from "icons/search.svg";
import * as styles from "./styles.module.scss";
const SearchSelectInput = ({ value, onChange, onFocus, onBlur, onQueryChange, onItemSelectExternal, items, className, componentClasses, readonly, undefinedReadonlyPlaceholder, placeholder, rightElement, itemPredicate, itemListPredicate, useExternalQueryForPredicate, renderAdditionalListItem, itemEqual = "label", noResultsRenderer, isHtmlValueLabel, resetOnClose, autoFocus, listProps, }) => {
    const inputRef = useRef(null);
    const [internalItems, setInternalItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState();
    const [itemToSave, setItemToSave] = useState();
    const [query, setQuery] = useState();
    const [inputFocused, setInputFocused] = useState(false);
    const itemsToSelect = useMemo(() => internalItems.map((item) => ({
        ...item,
        className: classNames(item.className, styles.item, { [styles.groupName]: item.isGroupName }),
    })), [internalItems]);
    const placeholderText = useMemo(() => {
        if (readonly && undefinedReadonlyPlaceholder) {
            return INPUT.UNDEFINED;
        }
        return placeholder;
    }, [placeholder, readonly, undefinedReadonlyPlaceholder]);
    const onItemSelectInternal = useCallback((item) => setItemToSave(item), []);
    const onClosing = useCallback(() => {
        if (!resetOnClose && itemToSave && itemToSave.value !== selectedItem?.value) {
            setSelectedItem(itemToSave);
            onChange(itemToSave.value);
        }
        onBlur();
        if (resetOnClose) {
            return;
        }
        setQuery(isHtmlValueLabel ? "" : itemToSave?.label || "");
    }, [itemToSave, selectedItem?.value, onBlur, isHtmlValueLabel, onChange, resetOnClose]);
    useEffect(() => {
        !resetOnClose && setQuery(isHtmlValueLabel ? "" : value?.label);
        if (selectedItem?.label !== value) {
            setSelectedItem(value);
            // Kostyl', currently I don't know why this was added
        }
    }, [isHtmlValueLabel, value, resetOnClose, selectedItem?.label]);
    useEffect(() => {
        setInternalItems(items);
    }, [items]);
    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        if (inputRef?.current) {
            setTimeout(() => inputRef?.current?.focus(), 300);
        }
    }, [autoFocus]);
    return (React.createElement("div", { className: classNames(styles.root, className) },
        React.createElement(Suggest, { viewMode: readonly, items: itemsToSelect, selectedItem: selectedItem, onItemSelect: (listItem) => {
                onItemSelectInternal(listItem);
                onItemSelectExternal && onItemSelectExternal(listItem);
            }, classes: {
                popoverClassName: componentClasses?.popoverClassName,
                portalClassName: classNames(styles.selectPortal),
                className: classNames(styles.select, {
                    [styles.hidden]: isHtmlValueLabel && !query?.length,
                    [styles.viewMode]: readonly,
                }),
            }, noResultsRenderer: noResultsRenderer ?? defaultNoResultsRenderer, query: query, itemEqual: itemEqual, onQueryChange: (queryString) => {
                setQuery(queryString);
                onQueryChange && onQueryChange(queryString);
            }, inputProps: {
                readOnly: readonly,
                inputRef,
                placeholder: placeholderText,
                onFocus: () => {
                    setInputFocused(true);
                    onFocus();
                },
                onBlur: () => setInputFocused(false),
            }, popoverProps: { onClosing, usePortal: true, matchTargetWidth: true }, showRemainingSymbols: false, rightElement: rightElement ?? React.createElement(SearchIcon, { className: styles.searchIcon }), itemPredicate: itemPredicate, itemListPredicate: itemListPredicate, useExternalQueryForPredicate: useExternalQueryForPredicate, renderAdditionalListItem: renderAdditionalListItem, disableCreateNew: true, resetOnClose: resetOnClose, listProps: listProps }),
        !query?.length && isHtmlValueLabel && (React.createElement(TextTruncate, { className: classNames(styles.htmlValueLabelRenderer, {
                [styles["htmlValueLabelRenderer-viewMode"]]: readonly,
                [styles["htmlValueLabelRenderer-placeholder"]]: inputFocused || isNotEmpty(value, true),
            }), text: selectedItem?.label ?? placeholderText }))));
};
export default SearchSelectInput;
